import React from "react";
import { Competency } from "../../../../models/Competency";
import { I18n } from "../../../../models/I18n";
import ReportSummarySection from "../ReportSummarySection/ReportSummarySection";
import { ReportSummaryColors } from "../../constants/ReportSummaryColors";
import { Language } from "../../../../constants/Language";
import { getCompetencyName } from "../../../../utils/getCompetencyName";
import competencyIcons from "./style/CompetencyIcons.module.scss";
import style from "./style/CompetencySummary.module.scss";

export interface CompetencySummaryProps {
    title: string;
    basedOn: string;
    handle: string;
    competencies: Competency[];
    i18n: I18n;
    languageId: Language;
    scrollToSection?: (elementId: string) => void;
    classes?: {
        competencies: string;
        titleDecoration: string;
    }
}

const CompetencySummary = ({
    title,
    basedOn,
    handle,
    competencies,
    i18n,
    languageId,
    scrollToSection,
    classes = { competencies: '', titleDecoration: '' }
}: CompetencySummaryProps) => {
    const basedOnText = basedOn || i18n.report_section_based_on_default_text;

    const getScoreLevelWithoutCutoff = (score: number, cutoff: number) => {
        if (cutoff) {
            return score < cutoff
                ? i18n.report_management_summary_competency_profile_not_passed
                : i18n.report_management_summary_competency_profile_passed;
        }

        switch (score) {
            case 1:
            case 1.5:
            case 2:
                return i18n.report_management_summary_competency_profile_below_average;
            case 2.5:
            case 3:
            case 3.5:
                return i18n.report_management_summary_competency_profile_average;
            case 4:
            case 4.5:
            case 5:
                return i18n.report_management_summary_competency_profile_above_average;
            default:
                return "";
        }
    };

    const getIconColors = (score: number, cutoff: number) => {
        const colors = {
            background: "",
            icon: "",
        };

        if ((cutoff && score < cutoff) || (!cutoff && score <= 2)) {
            colors.background = ReportSummaryColors.ORANGE;
            colors.icon = ReportSummaryColors.ORANGE_LIGHT;
        } else {
            colors.background = ReportSummaryColors.GREEN;
            colors.icon = ReportSummaryColors.GREEN_LIGHT;
        }

        return colors;
    };

    const getLocalCompetencies = (items: Competency[]) => {
        return items.map((competency: Competency) => {
            const scoreLabel = getScoreLevelWithoutCutoff(competency.score, competency.cutoff);
            const iconColor = getIconColors(competency.score, competency.cutoff);

            return {
                handle: competency.handle,
                comment: competency.comment,
                icon: `icon_${competency.translationKeyName}`,
                iconColor,
                name: getCompetencyName(competency, languageId, i18n),
                scoreLabel,
            };
        });
    };

    return (
        <ReportSummarySection
            basedOn={basedOnText}
            handle={handle}
            i18n={i18n}
            scrollToSection={scrollToSection ? scrollToSection : null}
            showViewAll={!!scrollToSection}
            title={title}
            classes={{ titleDecoration: classes?.titleDecoration || "" }}
        >
            <div className={classes && classes.competencies}>
                {getLocalCompetencies(competencies).map((competency) => (
                    <div key={competency.handle} className={style.competencyItem}>
                        <div className={style.competencyDescription}>
                            <div
                                className={style.competencyIcon}
                                data-testid="competency-icon"
                                style={
                                    {
                                        "--competency-icon-color": competency.iconColor.background,
                                        "--competency-icon-color-lighten": competency.iconColor.icon,
                                    } as React.CSSProperties
                                }
                            >
                                <figure>
                                    <i className={`${competencyIcons.icon} ${competencyIcons[competency.icon]}`} />
                                </figure>
                            </div>
                            <div>
                                <h5>{competency.name}</h5>
                                <p>{competency.scoreLabel}</p>
                            </div>
                        </div>
                        {competency.comment && <div className={style.comment}>
                            <h6>{`${i18n.report_summary_competency_profile_remark}: `}</h6><span dangerouslySetInnerHTML={{ __html: competency.comment}} />
                        </div>}
                    </div>
                ))}
            </div>
        </ReportSummarySection>
    );
};

export default CompetencySummary;
